import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { HiCheck } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { FiChevronDown, FiHeart, FiFilter, FiX } from "react-icons/fi";
import { FaHeart, FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import { addToCart } from "../../feature/cartSlice";
import { toggleWishlist } from "../../feature/wishlist";
import Banner from "../../assets/pexels-antoni-shkraba-6261903_v2-scaled.webp";
import fallbackImg from "../../assets/Ashwagandha Botanical Supplement Showcase.png";

/* ---------------- API ---------------- */
const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");

/* "abc.png" -> BASE/uploads/abc.png, "uploads/abc.png" -> BASE/uploads/abc.png */
const toImageUrl = (path) => {
  const src = typeof path === "string" ? path : path?.url;
  if (!src) return fallbackImg;
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  const clean = String(src).replace(/^\/+/, "");
  return clean.includes("/") ? `${BASE_API}/${clean}` : `${BASE_API}/uploads/${clean}`;
};

/* Converts one API product into the shape this page uses.
   If your API field names are different, change them only here. */
const normalizeProduct = (p) => {
  const price = Number(p.price ?? p.sellingPrice ?? p.salePrice ?? 0);
  const mrpRaw = p.mrp ?? p.originalPrice ?? p.oldPrice ?? p.old_price ?? null;
  const mrp = mrpRaw && Number(mrpRaw) > price ? Number(mrpRaw) : null;
  const firstImage = Array.isArray(p.images) ? p.images[0] : null;

  // category can be: object {id,name}, a category id, a category name, or category_id
  const catObj = p.category && typeof p.category === "object" ? p.category : null;
  const catString = typeof p.category === "string" ? p.category : "";
  const categoryId =
    p.categoryId ?? p.category_id ?? catObj?.id ?? catObj?._id ?? catString;
  const categoryName =
    p.categoryName ?? p.category_name ?? catObj?.name ?? catString;

  return {
    id: p.id ?? p._id,
    name: p.name ?? p.title ?? "Untitled product",
    category: categoryName ? String(categoryName) : "",
    categoryId: categoryId !== undefined && categoryId !== null ? String(categoryId) : "",
    brand: p.brand?.name ?? p.brand ?? "Others",
    price,
    mrp,
    rating: Number(p.rating ?? p.averageRating ?? 0),
    reviews: Number(p.reviews ?? p.reviewCount ?? p.numReviews ?? 0),
    img: toImageUrl(p.image ?? p.img ?? p.thumbnail ?? firstImage),
  };
};

/* Accepts [..], { products: [..] }, { data: [..] }, { data: { products: [..] } } ... */
const extractList = (json) => {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.products)) return json.products;
  if (Array.isArray(json?.categories)) return json.categories;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.data?.products)) return json.data.products;
  if (Array.isArray(json?.data?.categories)) return json.data.categories;
  if (Array.isArray(json?.items)) return json.items;
  return [];
};

const countBy = (list, key) => {
  const counts = {};
  list.forEach((p) => {
    counts[p[key]] = (counts[p[key]] || 0) + 1;
  });
  return Object.keys(counts)
    .sort()
    .map((name) => ({ name, count: counts[name] }));
};

const PRICE_RANGES = [
  { label: "₹0 – ₹500", min: 0, max: 500 },
  { label: "₹501 – ₹1,000", min: 501, max: 1000 },
  { label: "₹1,001 – ₹2,000", min: 1001, max: 2000 },
  { label: "₹2,001 – ₹5,000", min: 2001, max: 5000 },
];

const RATING_OPTIONS = [4, 3, 2, 1];

const SORT_OPTIONS = [
  { value: "popular", label: "Popularity" },
  { value: "low", label: "Price: low to high" },
  { value: "high", label: "Price: high to low" },
  { value: "rating", label: "Customer rating" },
  { value: "name", label: "Name: A to Z" },
];

const PER_PAGE = 9;

function Stars({ value, size = 11 }) {
  return (
    <span className="inline-flex items-center gap-[2px] text-[#f2a916]">
      {[1, 2, 3, 4, 5].map((n) => {
        if (value >= n) return <FaStar key={n} size={size} />;
        if (value >= n - 0.5) return <FaStarHalfAlt key={n} size={size} />;
        return <FaRegStar key={n} size={size} className="text-[#d6c79a]" />;
      })}
    </span>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div className="py-4 border-b border-[#e3e9dd] last:border-b-0">
      <h3 className="text-[14px] font-semibold text-[#1f4a2b] mb-3">{title}</h3>
      {children}
    </div>
  );
}

function CheckRow({ checked, onChange, label, count }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-[13px] text-[#3d4a3f] hover:text-[#1f4a2b]">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-[14px] h-[14px] accent-[#2f6b3a] cursor-pointer"
      />
      <span className="flex-1">{label}</span>
      {count !== undefined && (
        <span className="text-[11px] text-[#8a968c]">({count})</span>
      )}
    </label>
  );
}

function SkeletonCard() {
  return (
    <div className="flex flex-col bg-white border border-[#e3e9dd] rounded-lg overflow-hidden animate-pulse">
      <div className="w-full aspect-square bg-[#eef2e8]" />
      <div className="p-3 sm:p-4 flex flex-col gap-2">
        <div className="h-[10px] w-1/3 bg-[#eef2e8] rounded" />
        <div className="h-[14px] w-4/5 bg-[#eef2e8] rounded" />
        <div className="h-[10px] w-1/2 bg-[#eef2e8] rounded" />
        <div className="h-[16px] w-1/3 bg-[#eef2e8] rounded mb-2" />
        <div className="h-[34px] w-full bg-[#eef2e8] rounded-md" />
      </div>
    </div>
  );
}

function Singlecatgory() {
  const { id } = useParams();

  const [products, setProducts] = useState([]);
  const [categoryName, setCategoryName] = useState("Category");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedPrices, setSelectedPrices] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState("popular");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const wishItems = useSelector((state) => state.wishlist.items);

  /* Fetch /category (for the name) and /products, then keep only this category */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const [catRes, prodRes] = await Promise.all([
          fetch(`${BASE_API}/category`, { signal }).catch((e) => {
            if (e.name === "AbortError") throw e;
            return null; // category name is optional, don't fail the page
          }),
          fetch(`${BASE_API}/products`, { signal }),
        ]);

        if (!prodRes.ok) throw new Error(`Request failed (${prodRes.status})`);

        // find this category in the category list
        let currentCat = null;
        if (catRes && catRes.ok) {
          const catList = extractList(await catRes.json());
          currentCat =
            catList.find((c) => String(c.id ?? c._id) === String(id)) || null;
        }
        const catName = currentCat?.name ?? currentCat?.title ?? "";

        const all = extractList(await prodRes.json()).map(normalizeProduct);

        // match by category id OR by category name
        const matched = all.filter(
          (p) =>
            p.categoryId === String(id) ||
            (catName && p.category.toLowerCase() === catName.toLowerCase()),
        );

        setCategoryName(catName || matched[0]?.category || "Category");
        setProducts(matched);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message || "Something went wrong while loading products.");
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [id, reloadKey]);

  /* Reset filters when the category changes */
  useEffect(() => {
    setSelectedPrices([]);
    setSelectedBrands([]);
    setMinRating(0);
    setSort("popular");
    setPage(1);
    window.scrollTo(0, 0);
  }, [id]);

  const brands = useMemo(() => countBy(products, "brand"), [products]);

  const toggle = (setter, value) => {
    setter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
    setPage(1);
  };

  const hasFilters =
    selectedPrices.length > 0 || selectedBrands.length > 0 || minRating > 0;

  const clearAll = () => {
    setSelectedPrices([]);
    setSelectedBrands([]);
    setMinRating(0);
    setPage(1);
  };

  const filtered = useMemo(() => {
    let list = products.filter(
      (p) =>
        (selectedBrands.length === 0 || selectedBrands.includes(p.brand)) &&
        (selectedPrices.length === 0 ||
          selectedPrices.some(
            (i) => p.price >= PRICE_RANGES[i].min && p.price <= PRICE_RANGES[i].max,
          )) &&
        p.rating >= minRating,
    );
    if (sort === "popular") list = [...list].sort((a, b) => b.reviews - a.reviews);
    if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    if (sort === "name")
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, selectedPrices, selectedBrands, minRating, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const start = (page - 1) * PER_PAGE;
  const visible = filtered.slice(start, start + PER_PAGE);

  return (
    <div className="w-full bg-[#f6f8f2] pt-[60px] min-h-screen">
      {/* Hero banner */}
      <section className="relative w-full overflow-hidden bg-[#1f4a2b]">
        <img
          src={Banner}
          alt=""
          aria-hidden="true"
          className="absolute right-0 top-0 h-full w-[70%] sm:w-[55%] object-cover object-right opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1f4a2b] via-[#1f4a2b]/90 to-transparent" />
        <div className="relative w-full max-w-[1240px] mx-auto px-4 py-8 sm:py-12">
          <h1 className="font-serif text-[26px] sm:text-[36px] text-white leading-tight">
            {loading ? "Loading…" : categoryName}
          </h1>
          <p className="text-[13px] sm:text-[15px] text-[#d6e6d0] mt-2">
            Natural care for a healthier tomorrow
          </p>
        </div>
      </section>

      <div className="w-full max-w-[1240px] mx-auto px-4 py-6 sm:py-8">
        <p className="text-[12px] text-[#6b7a6d] mb-4">
          <Link to="/" className="hover:text-[#2f6b3a]">
            Home
          </Link>{" "}
          /{" "}
          <Link to="/shop" className="hover:text-[#2f6b3a]">
            Shop
          </Link>{" "}
          / <span className="text-[#1f4a2b]">{loading ? "…" : categoryName}</span>
        </p>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Mobile filter toggle */}
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="lg:hidden flex items-center justify-center gap-2 bg-white border border-[#e3e9dd] rounded-md py-2 text-[13px] text-[#1f4a2b] font-medium"
          >
            {showFilters ? <FiX /> : <FiFilter />}
            {showFilters ? "Hide filters" : "Show filters"}
          </button>

          {/* Sidebar */}
          <aside
            className={`${showFilters ? "block" : "hidden"} lg:block w-full lg:w-[240px] shrink-0`}
          >
            <div className="bg-white border border-[#e3e9dd] rounded-lg px-5 py-2">
              <FilterGroup title="Price Range">
                <ul className="flex flex-col gap-2">
                  {PRICE_RANGES.map((r, i) => (
                    <li key={r.label}>
                      <CheckRow
                        checked={selectedPrices.includes(i)}
                        onChange={() => toggle(setSelectedPrices, i)}
                        label={r.label}
                      />
                    </li>
                  ))}
                </ul>
              </FilterGroup>

              {brands.length > 0 && (
                <FilterGroup title="Brand">
                  <ul className="flex flex-col gap-2">
                    {brands.map((b) => (
                      <li key={b.name}>
                        <CheckRow
                          checked={selectedBrands.includes(b.name)}
                          onChange={() => toggle(setSelectedBrands, b.name)}
                          label={b.name}
                          count={b.count}
                        />
                      </li>
                    ))}
                  </ul>
                </FilterGroup>
              )}

              <FilterGroup title="Rating">
                <ul className="flex flex-col gap-2">
                  {RATING_OPTIONS.map((r) => (
                    <li key={r}>
                      <label className="flex items-center gap-2 cursor-pointer text-[13px] text-[#3d4a3f]">
                        <input
                          type="checkbox"
                          checked={minRating === r}
                          onChange={() => {
                            setMinRating(minRating === r ? 0 : r);
                            setPage(1);
                          }}
                          className="w-[14px] h-[14px] accent-[#2f6b3a] cursor-pointer"
                        />
                        <Stars value={r} />
                        <span className="text-[12px] text-[#6b7a6d]">&amp; up</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </FilterGroup>

              {hasFilters && (
                <div className="py-4">
                  <button
                    type="button"
                    onClick={clearAll}
                    className="w-full border border-[#2f6b3a] text-[#2f6b3a] hover:bg-[#2f6b3a] hover:text-white transition-colors text-[12px] font-semibold rounded-md py-2"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </aside>

          {/* Products */}
          <main className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <h2 className="text-[17px] sm:text-[19px] font-semibold text-[#1f4a2b]">
                {loading ? "Products" : categoryName}{" "}
                <span className="text-[#6b7a6d] font-normal text-[14px]">
                  ({loading ? "…" : filtered.length})
                </span>
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-[#6b7a6d] whitespace-nowrap">
                  Sort by:
                </span>
                <div className="relative w-full sm:w-[170px]">
                  <select
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value);
                      setPage(1);
                    }}
                    className="w-full appearance-none bg-white border border-[#d6e0d0] text-[13px] text-[#1f4a2b] px-3 py-2 pr-8 rounded-md cursor-pointer focus:outline-none focus:border-[#2f6b3a]"
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-[#1f4a2b] pointer-events-none" />
                </div>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
                {Array.from({ length: PER_PAGE }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : error ? (
              <div className="bg-white border border-[#e3e9dd] rounded-lg text-center py-16 px-4">
                <p className="text-[#d9534f] text-[14px] mb-4">{error}</p>
                <button
                  type="button"
                  onClick={() => setReloadKey((k) => k + 1)}
                  className="bg-[#2f6b3a] hover:bg-[#1f4a2b] text-white text-[13px] font-semibold rounded-md px-5 py-2 transition-colors"
                >
                  Try again
                </button>
              </div>
            ) : visible.length === 0 ? (
              <div className="bg-white border border-[#e3e9dd] rounded-lg text-center text-[#6b7a6d] py-16">
                No products found in this category.
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
                {visible.map((p) => {
                  const inCart = cartItems.some((i) => i.id === p.id);
                  const inWishlist = wishItems.some((i) => i.id === p.id);
                  return (
                    <div
                      key={p.id}
                      className="group relative flex flex-col bg-white border border-[#e3e9dd] rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300"
                    >
                      <Link to={`/product/${p.id}`} className="block">
                        <div className="w-full aspect-square bg-[#f4f7ef] overflow-hidden">
                          <img
                            src={p.img}
                            alt={p.name}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = fallbackImg;
                            }}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      </Link>

                      <button
                        type="button"
                        aria-label={
                          inWishlist ? "Remove from wishlist" : "Add to wishlist"
                        }
                        title={
                          inWishlist ? "Remove from wishlist" : "Add to wishlist"
                        }
                        onClick={() =>
                          dispatch(
                            toggleWishlist({
                              id: p.id,
                              name: p.name,
                              price: p.price,
                              image: p.img,
                              category: p.category,
                            }),
                          )
                        }
                        className="absolute top-2 right-2 z-10 w-[32px] h-[32px] rounded-full bg-white/95 shadow flex items-center justify-center text-[#1f4a2b] hover:text-[#d9534f] transition-colors"
                      >
                        {inWishlist ? (
                          <FaHeart className="text-[14px] text-[#d9534f]" />
                        ) : (
                          <FiHeart className="text-[14px]" />
                        )}
                      </button>

                      <div className="flex flex-col flex-1 p-3 sm:p-4">
                        {p.category && (
                          <p className="text-[11px] text-[#7a8a7c] mb-1">
                            {p.category}
                          </p>
                        )}
                        <Link
                          to={`/product/${p.id}`}
                          className="text-[14px] sm:text-[15px] font-semibold text-[#1f2d22] leading-snug hover:text-[#2f6b3a]"
                        >
                          {p.name}
                        </Link>

                        <div className="flex items-center gap-1.5 mt-2">
                          <Stars value={p.rating} />
                          <span className="text-[11px] text-[#7a8a7c]">
                            {p.rating} ({p.reviews})
                          </span>
                        </div>

                        <div className="flex items-baseline gap-2 mt-2 mb-3">
                          <span className="text-[16px] font-bold text-[#1f2d22]">
                            ₹{p.price}
                          </span>
                          {p.mrp && (
                            <span className="text-[12px] text-[#9aa79c] line-through">
                              ₹{p.mrp}
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            dispatch(
                              addToCart({
                                id: p.id,
                                name: p.name,
                                price: p.price,
                                image: p.img,
                                qty: 1,
                              }),
                            )
                          }
                          className={`mt-auto w-full flex items-center justify-center gap-1.5 rounded-md py-2 text-[12px] sm:text-[13px] font-semibold transition-colors ${
                            inCart
                              ? "bg-[#1f4a2b] text-white"
                              : "bg-[#2f6b3a] text-white hover:bg-[#1f4a2b]"
                          }`}
                        >
                          {inCart ? (
                            <>
                              <HiCheck className="text-[16px]" /> Added to Cart
                            </>
                          ) : (
                            "Add to Cart"
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!loading && !error && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`w-[32px] h-[32px] text-[12px] rounded-md border border-[#2f6b3a] ${
                      page === n
                        ? "bg-[#2f6b3a] text-white"
                        : "bg-white text-[#2f6b3a] hover:bg-[#2f6b3a] hover:text-white"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                {page < totalPages && (
                  <button
                    type="button"
                    onClick={() => setPage(page + 1)}
                    className="w-[32px] h-[32px] text-[12px] rounded-md border border-[#2f6b3a] bg-white text-[#2f6b3a] hover:bg-[#2f6b3a] hover:text-white"
                  >
                    →
                  </button>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default Singlecatgory;
